/**
 * Patch 35.2b-ii · Rendered Video Preview
 *
 * Fetches a short-lived signed URL for a rendered video asset (v2 head
 * of a video_production_kit) and plays it inline via HTML5 ``<video>``.
 * Mirrors :class:`RenderedImagePreview` but adds video-specific
 * loading / unavailable / expired states and a poster placeholder for
 * providers that don't ship a thumbnail.
 *
 * NEVER surfaces provider names, storage keys, checksums, or costs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2, RefreshCcw, Video } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import { fetchSignedMediaUrl } from "@/entities/ai-studio/api";

interface Props {
  workspaceId: string;
  assetId: string;
  className?: string;
}

export function RenderedVideoPreview({
  workspaceId, assetId, className,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [videoFailed, setVideoFailed] = useState(false);
  const q = useQuery({
    queryKey: ["media-engine-signed-url", workspaceId, assetId],
    queryFn: () => fetchSignedMediaUrl(workspaceId, assetId),
    enabled: !!workspaceId && !!assetId,
    staleTime: 4 * 60 * 1000,
  });
  const refresh = useMutation({
    mutationFn: async () => {
      setVideoFailed(false);
      await qc.invalidateQueries({
        queryKey: ["media-engine-signed-url", workspaceId, assetId],
      });
    },
    onSuccess: () => {
      toast.success("Video preview refreshed");
    },
  });

  if (q.isLoading) {
    return (
      <div
        data-testid="rendered-video-loading"
        className={`flex items-center justify-center rounded-md border border-border/50 bg-secondary/30 p-8 ${className ?? ""}`}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span className="text-xs text-muted-foreground">Loading video…</span>
      </div>
    );
  }

  if (q.isError) {
    const anyErr = q.error as unknown as { status?: number };
    const notFound = anyErr?.status === 404;
    return (
      <div
        data-testid={notFound ? "rendered-video-unavailable" : "rendered-video-error"}
        className={`flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-background/60 p-6 text-center text-xs ${className ?? ""}`}
      >
        <AlertCircle className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">
          {notFound ? "No rendered video attached yet." : "Could not load video. Try refreshing."}
        </span>
        <Button
          data-testid="rendered-video-retry"
          size="sm" variant="outline"
          onClick={() => refresh.mutate()}
        >
          <RefreshCcw className="mr-1 h-3 w-3" /> Retry
        </Button>
      </div>
    );
  }

  const signed = q.data;
  if (!signed || !signed.url) {
    return (
      <div
        data-testid="rendered-video-unavailable"
        className={`flex items-center justify-center rounded-md border border-dashed border-border/60 bg-background/60 p-6 text-center text-xs text-muted-foreground ${className ?? ""}`}
      >
        <Video className="mr-2 h-4 w-4" /> No video available.
      </div>
    );
  }

  if (videoFailed) {
    return (
      <div
        data-testid="rendered-video-expired"
        className={`flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-background/60 p-6 text-center text-xs ${className ?? ""}`}
      >
        <AlertCircle className="h-4 w-4 text-amber-500" />
        <span className="text-muted-foreground">Video link expired or unreachable.</span>
        <Button
          data-testid="rendered-video-retry"
          size="sm" variant="outline"
          onClick={() => refresh.mutate()}
        >
          <RefreshCcw className="mr-1 h-3 w-3" /> Refresh preview
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="rendered-video-preview"
      className={`overflow-hidden rounded-md border border-border/60 bg-black ${className ?? ""}`}
    >
      <video
        data-testid="rendered-video-preview-player"
        src={signed.url}
        controls
        preload="metadata"
        className="mx-auto max-h-96 w-full object-contain"
        onError={() => setVideoFailed(true)}
      />
      <div
        data-testid="rendered-video-preview-meta"
        className="flex items-center justify-between border-t border-border/40 bg-secondary/20 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"
      >
        <span>{signed.mime_type}</span>
        {signed.width && signed.height ? (
          <span>{signed.width} × {signed.height}</span>
        ) : null}
      </div>
    </div>
  );
}
